/**
 * CalendarList — /calendars
 *
 * Three tabs: Discover (public), Mine (owner), Following.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useSEO } from '../useSEO';
import { calendarsApi } from './api';

const TABS = [
  { id: 'discover', label: 'Discover' },
  { id: 'mine', label: 'My calendars' },
  { id: 'following', label: 'Following' },
];

function CalendarCard({ cal }) {
  return (
    <Link to={`/calendars/${cal.shareSlug}`} className="cal-card">
      <div className="cal-card-emoji" aria-hidden>{cal.coverEmoji || '📅'}</div>
      <div className="cal-card-body">
        <h3 className="cal-card-title">{cal.title}</h3>
        {cal.ownerName && <div className="cal-card-owner">by {cal.ownerName}</div>}
        {cal.description && <p className="cal-card-desc">{cal.description}</p>}
        <div className="cal-card-meta">
          <span className={`cal-tag cal-tag-${cal.visibility || 'public'}`}>
            {cal.visibility === 'unlisted' ? 'Link only' : 'Public'}
          </span>
          {typeof cal.dayCount === 'number' && <span className="cal-card-stat">{cal.dayCount} day{cal.dayCount === 1 ? '' : 's'}</span>}
          {typeof cal.followerCount === 'number' && cal.followerCount > 0 && (
            <span className="cal-card-stat">{cal.followerCount} follower{cal.followerCount === 1 ? '' : 's'}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CalendarList() {
  useSEO({
    title: 'Revision calendars',
    description: 'Browse, follow and create shareable revision schedules. Tutors can plan their students’ weeks; students can follow ready-made GCSE and A-Level study plans.',
    canonicalPath: '/calendars',
  });

  const { token, user } = useAuth();
  const [tab, setTab] = useState('discover');
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let res;
      if (tab === 'discover') res = await calendarsApi.discover(token);
      else if (tab === 'mine') res = await calendarsApi.mine(token);
      else res = await calendarsApi.following(token);
      setCalendars(res.calendars || []);
    } catch (err) {
      setError(err.message || 'Failed to load calendars');
      setCalendars([]);
    } finally {
      setLoading(false);
    }
  }, [tab, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="tt-page cal-list-page">
      <header className="tt-header">
        <div>
          <h1>Revision calendars</h1>
          <p className="tt-sub">Browse public revision plans, follow your tutor’s schedule, or build your own and share the link.</p>
        </div>
        <div className="tt-header-actions">
          {user ? (
            <Link to="/calendars/new" className="primary-btn cal-new-btn">
              <span aria-hidden style={{ fontSize: '1.1rem', lineHeight: 1 }}>＋</span>
              New calendar
            </Link>
          ) : (
            <Link to={`/login?next=${encodeURIComponent('/calendars/new')}`} className="primary-btn cal-new-btn">
              <span aria-hidden style={{ fontSize: '1.1rem', lineHeight: 1 }}>＋</span>
              Create a calendar
            </Link>
          )}
        </div>
      </header>

      <div className="cal-tabs" role="tablist">
        {TABS.map((t) => {
          const disabled = (t.id === 'mine' || t.id === 'following') && !user;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              disabled={disabled}
              className={`cal-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {error && <div className="tt-status error">{error}</div>}
      {loading ? (
        <div className="tt-status saving">Loading…</div>
      ) : calendars.length === 0 ? (
        <div className="tt-empty">
          <p>
            {tab === 'discover' && 'No public calendars yet — be the first to share one!'}
            {tab === 'mine' && (user ? 'You haven’t created any calendars yet.' : 'Log in to create calendars.')}
            {tab === 'following' && 'You aren’t following any calendars yet. Open a shared link and tap Follow.'}
          </p>
          {tab !== 'following' && user && <Link to="/calendars/new" className="primary-btn">Create a calendar</Link>}
        </div>
      ) : (
        <div className="cal-grid">
          {calendars.map((c) => <CalendarCard key={c.id} cal={c} />)}
        </div>
      )}
    </div>
  );
}
